import {
  CONTRACT_FIELDS,
  CONTRACT_GROUP_ORDER,
  NO_FIELD_HISTORY,
  fieldCommentTarget,
  isRequired,
  needsConfirmation,
  onWorksheet,
  settledAnswer,
  unsettledReason,
} from '@worker/policy-builder/client'
import type {
  AnswerValue,
  Citation,
  CitationKind,
  ContractField,
  ContractKind,
  ContractStatus,
  ContractSuggestion,
  ContractVersion,
  ContractVersionOrigin,
  FieldHistoryState,
  FieldProposal,
  FieldResolution,
  PolicyDocView,
  PolicyNote,
  PolicyQuestion,
  ProposalSource,
} from '@worker/policy-builder/client'
import type { ResolvedComment } from '@rialtic/nuxt-shared'
import { isEqual } from 'ohash'
import type { WhyEntry } from '~/utils/policy-studio/scoping'
import type {
  WorkRailGroup,
  WorkRailItem,
} from '~/utils/policy-studio/workRail'
import type { PdfSelection } from '~/utils/refDocUtils'

/* The scoping worksheet's rows, read from a policy room rather than fixtures.
   Settled-ness comes from the worker's own `unsettledReason`, so a row the
   page shows as accepted is exactly one the scope gate lets through. */

export interface ContractRowView {
  field: ContractField
  status: ContractStatus
  /** Answered or marked not applicable, as the scope gate judges it; a
   *  citation is optional (S8). */
  settled: boolean
  /** Asked of this policy, so the scope gate counts it. A row for another
   *  Logic Type is still shown (S-N6), but never holds the hand-over. */
  applies: boolean
  /** Why scoping marked the row not applicable; it stays in the worksheet. */
  notApplicable: { reason: string } | null
  answer: AnswerValue | undefined
  citations: Citation[]
  questions: PolicyQuestion[]
  /** This row's suggestions, newest first. */
  suggestions: ContractSuggestion[]
  threads: CommentThread[]
  /** Notes on this row, oldest first. Not evidence: they never settle a row. */
  notes: PolicyNote[]
  history: FieldHistoryState
  /** The machine's own proposed value for this field, if it made one. */
  proposal: FieldProposal | undefined
  /** How the proposal was resolved — `null` when there is no proposal to resolve. */
  resolution: FieldResolution | null
}

export interface CommentThread {
  root: ResolvedComment
  replies: ResolvedComment[]
}

/** A thread's latest activity: its own post, or its newest reply. */
export const threadActivityAt = (thread: CommentThread): number =>
  Math.max(thread.root.createdAt, ...thread.replies.map((r) => r.createdAt))

/** A row's threads: open ones first, each group oldest first; replies in time order. */
export const commentThreads = (
  comments: ResolvedComment[],
  fieldId: string,
): CommentThread[] => {
  const target = fieldCommentTarget(fieldId)
  const byTime = (a: ResolvedComment, b: ResolvedComment) =>
    a.createdAt - b.createdAt
  return comments
    .filter((comment) => !comment.parentId && comment.target === target)
    .toSorted((a, b) => Number(a.resolved) - Number(b.resolved) || byTime(a, b))
    .map((root) => ({
      root,
      replies: comments
        .filter((comment) => comment.parentId === root.id)
        .toSorted(byTime),
    }))
}

/** Whether `me` is tagged anywhere in an open thread. */
export const taggedIn = (threads: CommentThread[], me: string): boolean => {
  const id = me.toLowerCase()
  return threads.some(
    ({ root, replies }) =>
      !root.resolved &&
      [root, ...replies].some((comment) => comment.mentions.includes(id)),
  )
}

const HIDDEN_KINDS = new Set<ContractKind>([
  'asked',
  'open',
  'gated',
  'notasked',
])

/* Source excerpts has no answer of its own: a cited source passage is what
   settles it, as the scope gate reads it. */
const SOURCE_EXCERPTS = 'source-excerpts'

/**
 * The rows asked of this policy: answerable, gated in, not moved to "not
 * asked". `withNotAsked` adds every row the contract holds back (S-N6), so
 * scoping works one checklist with every field on it.
 */
export const contractRowViews = (
  view: PolicyDocView,
  comments: ResolvedComment[] = [],
  {
    withNotApplicable = false,
    withNotAsked = false,
  }: { withNotApplicable?: boolean; withNotAsked?: boolean } = {},
): ContractRowView[] =>
  CONTRACT_FIELDS.filter(
    (field) =>
      (onWorksheet(field, view) ||
        (withNotAsked && HIDDEN_KINDS.has(field.kind))) &&
      (withNotApplicable || !(field.id in view.notAsked)),
  ).map((field) => {
    /* A client writes this map, so the entry may not have the shape it claims. */
    const skipped: unknown = view.notAsked[field.id]?.reason
    const notApplicable =
      field.id in view.notAsked
        ? { reason: typeof skipped === 'string' ? skipped : '' }
        : null
    const unsettled = unsettledReason(view, field)
    const answered =
      unsettled === null ||
      (unsettled === 'no citation' && field.id !== SOURCE_EXCERPTS)
    const settled = notApplicable ? !isRequired(field) : answered
    const applies = onWorksheet(field, view)
    return {
      field,
      settled,
      applies,
      notApplicable,
      status: settled
        ? 'Accepted'
        : isRequired(field)
          ? 'Needs answer'
          : applies && needsConfirmation(field)
            ? 'Confirm or N/A'
            : 'Optional',
      answer: view.answers[field.id],
      citations: view.citations.filter(
        (citation) => citation.fieldId === field.id,
      ),
      questions: view.questions.filter(
        (question) => question.fieldId === field.id,
      ),
      suggestions: view.suggestions
        .filter((suggestion) => suggestion.fieldId === field.id)
        .toReversed(),
      notes: view.notes.filter((note) => note.fieldId === field.id),
      threads: commentThreads(comments, field.id),
      history: view.fieldHistory[field.id] ?? NO_FIELD_HISTORY,
      proposal: view.proposals[field.id],
      resolution: view.fieldResolutions[field.id] ?? null,
    }
  })

/**
 * Rows the contract holds back from this policy: left out of the worksheet, or
 * gated out by the answers so far. Rows that are retired, inherited from the
 * source record, system-set or dropped are not the scoper's business and are
 * not listed.
 */
export const notAskedFields = (view: PolicyDocView) =>
  CONTRACT_FIELDS.filter(
    (field) =>
      HIDDEN_KINDS.has(field.kind) &&
      !onWorksheet(field, view),
  ).map((field) => ({ field, reason: field.note ?? field.gate ?? '' }))

/* `WhyBlock` names the three provenance voices for the reader; the room stores
   them as citation kinds. */
const VOICE: Record<CitationKind, WhyEntry['kind']> = {
  source: 'source',
  decision: 'wrote',
  inherited: 'house',
}
const KIND: Record<WhyEntry['kind'], CitationKind> = {
  source: 'source',
  wrote: 'decision',
  house: 'inherited',
}

export const whyEntries = (citations: Citation[]): WhyEntry[] =>
  citations.map((citation) => ({
    id: citation.id,
    kind: VOICE[citation.kind],
    text: citation.text,
    cite: citation.cite || undefined,
    by: citation.by || undefined,
    at: citation.at || undefined,
    anchored: Boolean(citation.anchor),
  }))

/**
 * A field's edited provenance, back as citations. An entry that already
 * existed keeps its source, excerpt and PDF anchor; a new one is stamped with
 * who wrote it and when.
 */
export const citationsFromWhy = (
  fieldId: string,
  entries: WhyEntry[],
  existing: Citation[],
  actor: string,
): Citation[] =>
  entries.map((entry) => ({
    ...(existing.find((citation) => citation.id === entry.id) ?? {
      referenceDocumentId: null,
      excerptId: null,
      anchor: null,
      by: actor,
      at: new Date().toISOString(),
    }),
    id: entry.id,
    fieldId,
    kind: KIND[entry.kind],
    text: entry.text,
    cite: entry.cite ?? '',
  }))

/* The policy's name and what it is for: everything below follows from them,
   so they lead the rail and the worksheet rather than sit inside a group. */
export const GATE_IDS = new Set(['policy-name', 'intent'])

/* What design and validation read of the scope besides the rows it settled:
   intent leads whatever its state, and scoping notes is optional free text
   that never settles (it carries no citation) but is still worth reading. */
export const ALWAYS_SHOWN_IDS = new Set(['intent', 'scoping-notes'])

export type RailSide = 'scoping' | 'design'

/* One contract row as a work-rail item, built the same way for scoping and
   design so both teams read one state and the same three counts per row. A
   question design raised waits on scoping until it is answered, then on
   design until it is accepted into the tree; `side` says whose turn it is. */
export const contractRailItem = (
  row: ContractRowView,
  {
    side,
    me,
    unread = false,
  }: { side: RailSide; me: string; unread?: boolean },
): WorkRailItem => {
  const open = row.questions.filter((question) => !question.closedAt)
  const asked = open.some((question) => question.answer === null)
  const answered = !asked && open.length > 0
  const questionOpen = side === 'scoping' ? asked : open.length > 0
  const answer = settledAnswer(row.field, row.answer)
  return {
    id: row.field.id,
    label: row.field.label,
    /* An unanswered row's state says so; a settled one that was left empty
       on purpose says that in words; a not-applicable one gives its reason. */
    value: row.notApplicable
      ? { text: row.notApplicable.reason || 'Not applicable', muted: true }
      : answer.muted && !row.settled
        ? undefined
        : answer,
    /* An unreviewed proposal reads as one even though it already answers the
       row; a row nothing waits on (optional, or for another Logic Type) is
       muted rather than counted as needing you. */
    state: questionOpen
      ? 'question'
      : row.notApplicable
        ? 'skipped'
        : row.resolution === 'proposed'
          ? 'proposed'
          : row.settled
            ? 'settled'
            : row.suggestions.some(
                  (suggestion) => suggestion.status === 'pending',
                )
              ? 'proposed'
              : row.status === 'Optional'
                ? 'skipped'
                : 'needs',
    meta: asked
      ? side === 'scoping'
        ? 'Design asked'
        : 'With scoping'
      : answered && side === 'design'
        ? 'Scoping answered'
        : row.notApplicable
          ? 'Not applicable'
          : row.status === 'Optional' && !row.settled
            ? row.applies
              ? 'Optional'
              : 'Not asked of this policy'
            : undefined,
    indicators: {
      decision: row.citations.filter((c) => c.kind === 'decision').length,
      discussion:
        row.notes.length +
        row.threads.filter((thread) => !thread.root.resolved).length,
    },
    waiting:
      unread ||
      taggedIn(row.threads, me) ||
      (side === 'scoping' ? asked : answered),
  }
}

/** Rows in the worksheet's order: the gate first, then each contract group. */
export const contractRailGroups = (
  rows: ContractRowView[],
  toItem: (row: ContractRowView) => WorkRailItem,
): WorkRailGroup[] =>
  [
    {
      key: 'start',
      label: 'Start here',
      rows: rows.filter((row) => GATE_IDS.has(row.field.id)),
    },
    ...CONTRACT_GROUP_ORDER.map((group) => ({
      key: group,
      label: group,
      rows: rows.filter(
        (row) => row.field.group === group && !GATE_IDS.has(row.field.id),
      ),
    })),
  ].flatMap(({ rows: groupRows, ...group }) =>
    groupRows.length ? [{ ...group, items: groupRows.map(toItem) }] : [],
  )

/**
 * The worksheet row to show when the one named is not among `ids` (rail
 * order): the row that took its place, if it just left (`previous` held it);
 * else (nothing named, or a name the worksheet never had) the oldest
 * still-open question's row, so a policy design returned a question on opens
 * on it (S-N9); else the first.
 */
export const landingRow = (
  ids: string[],
  named: string,
  previous: string[] | undefined,
  questions: PolicyQuestion[],
): string | undefined => {
  const left = previous?.indexOf(named) ?? -1
  if (left !== -1) return ids[Math.min(left, ids.length - 1)]
  return (
    questions.find(
      (question) => question.answer === null && ids.includes(question.fieldId),
    )?.fieldId ?? ids[0]
  )
}

/** The author id the worker stores on NURO's suggestions; shown as "NURO". */
export const NEURO_AUTHOR = 'Neuro'

/** Who proposed a value, as the page names them. */
export const authorLabel = (id: string): string => (id === NEURO_AUTHOR ? 'NURO' : id)

export const PROPOSED_BY: Record<ProposalSource, string> = {
  neuro: 'NURO',
  triage: 'Triage',
  system: 'Policy Studio',
  'content-library': 'Content Library',
}

export type RowActivity =
  | { kind: 'thread'; id: string; at: number; thread: CommentThread }
  | {
      kind: 'event'
      id: string
      at: number
      actor: string
      text: string
      /** What changed or was said, quoted under the line. */
      detail?: string
      icon: string
    }

const VERSION_TEXT: Record<ContractVersionOrigin, string> = {
  edit: 'changed the answer',
  suggestion: 'accepted a suggestion',
  restore: 'restored an earlier answer',
  confirm: 'confirmed the proposed answer',
}
const CITATION_TEXT: Record<CitationKind, string> = {
  source: 'cited the source',
  decision: 'recorded a decision',
  inherited: 'carried over a house rule',
}

/**
 * Everything that happened on one row, oldest first so the newest sits by the
 * composer: its proposal, each answer change (`versions`, the loaded page of
 * its history), suggestions, evidence, notes, questions, and its comment
 * threads in full. Accepting a suggestion shows as the change it made.
 */
export const rowActivity = (
  row: ContractRowView,
  versions: ContractVersion[],
): RowActivity[] => {
  const valueText = (value: AnswerValue | null) =>
    value === null ? 'Cleared' : settledAnswer(row.field, value).text
  const event = (
    id: string,
    at: string,
    actor: string,
    text: string,
    icon: string,
    detail?: string,
  ): RowActivity => ({
    kind: 'event',
    id,
    at: Date.parse(at),
    actor,
    text,
    icon,
    detail,
  })
  return [
    ...(row.proposal
      ? [
          event(
            'proposal',
            row.proposal.at,
            PROPOSED_BY[row.proposal.by],
            'proposed an answer',
            'i-ph-sparkle',
            valueText(row.proposal.value),
          ),
        ]
      : []),
    ...versions.map((version) =>
      event(
        `version:${version.id}`,
        version.at,
        version.by,
        `${VERSION_TEXT[version.origin]}${version.undone ? ' (undone)' : ''}`,
        'i-ph-pencil-simple',
        valueText(version.value),
      ),
    ),
    ...row.suggestions.flatMap((suggestion) => [
      event(
        `suggestion:${suggestion.id}`,
        suggestion.at,
        suggestion.by,
        'suggested an answer',
        'i-ph-lightbulb',
        valueText(suggestion.value),
      ),
      ...(suggestion.status === 'declined' && suggestion.resolvedAt
        ? [
            event(
              `declined:${suggestion.id}`,
              suggestion.resolvedAt,
              suggestion.resolvedBy ?? '',
              'declined a suggestion',
              'i-ph-x',
              suggestion.feedback ?? undefined,
            ),
          ]
        : []),
    ]),
    ...row.citations
      .filter((citation) => citation.by && citation.at)
      .map((citation) =>
        event(
          `citation:${citation.id}`,
          citation.at,
          citation.by,
          CITATION_TEXT[citation.kind],
          'i-ph-quotes',
          citation.text,
        ),
      ),
    ...row.notes.map((note) =>
      event(
        `note:${note.id}`,
        note.at,
        note.by,
        'added a note',
        'i-ph-note',
        note.body,
      ),
    ),
    ...row.questions.flatMap((question) => [
      event(
        `asked:${question.id}`,
        question.askedAt,
        question.askedBy,
        'asked scoping',
        'i-ph-question',
        question.text,
      ),
      ...(question.answer
        ? [
            event(
              `answered:${question.id}`,
              question.answer.at,
              question.answer.by,
              'answered',
              'i-ph-arrow-bend-down-right',
              question.answer.text,
            ),
          ]
        : []),
      ...(question.closedAt
        ? [
            event(
              `closed:${question.id}`,
              question.closedAt,
              question.closedBy ?? '',
              'closed the question',
              'i-ph-check',
            ),
          ]
        : []),
    ]),
    ...row.threads.map(
      (thread): RowActivity => ({
        kind: 'thread',
        id: thread.root.id,
        at: thread.root.createdAt,
        thread,
      }),
    ),
  ].toSorted((a, b) => a.at - b.at)
}

/** A not-applicable row's control is replaced by the reason it was marked;
 *  "Put back" brings the control back. */
export const openShapeSettledNote = (
  row: Pick<ContractRowView, 'notApplicable'>,
): string | undefined =>
  row.notApplicable
    ? `Not applicable${row.notApplicable.reason ? ` — ${row.notApplicable.reason}` : ''}`
    : undefined

export interface SuggestionEntry {
  key: string
  value: AnswerValue
  by: string
  at: string
  confidence: number | null
  reasoning: string
  cite: string
  /** `null` for NURO's original proposal, which is not a suggestion record. */
  suggestion: ContractSuggestion | null
}

/** A row's suggested answers, in display order: NURO's seeded proposal,
 *  kept listed after the field moves on so the row still shows what the AI
 *  said, followed by its still-pending suggestions. */
export const rowSuggestionEntries = (row: ContractRowView): SuggestionEntry[] => {
  const pendingEntries = row.suggestions
    .filter((suggestion) => suggestion.status === 'pending')
    .map((suggestion) => ({
      key: suggestion.id,
      value: suggestion.value,
      by: suggestion.by,
      at: suggestion.at,
      confidence: suggestion.confidence,
      reasoning: suggestion.rationale,
      cite: '',
      suggestion,
    }))
  if (row.proposal?.by !== 'neuro') return pendingEntries
  const source = row.citations.find((citation) => citation.kind === 'source')
  const proposal: SuggestionEntry = {
    key: 'proposal',
    value: row.proposal.value,
    by: NEURO_AUTHOR,
    at: row.proposal.at,
    confidence: null,
    reasoning: source?.text ?? '',
    cite: source?.cite ?? '',
    suggestion: null,
  }
  return [proposal, ...pendingEntries]
}

/** Whether `value` is the row's current answer, for muting a suggestion
 *  that only repeats it. */
export const isAnswerEqual = (
  value: AnswerValue,
  answer: AnswerValue | undefined,
): boolean => isEqual(value, answer ?? '')

/** Only NURO's suggestions can be steered, and only once the row still
 *  names the source candidate it came from. */
export const isSuggestionSteerable = (
  suggestion: ContractSuggestion,
  meta: { sourceId?: string | null; candidateId?: string | null },
): boolean =>
  suggestion.by === NEURO_AUTHOR && Boolean(meta.sourceId && meta.candidateId)

/** A design question's citation picker: the row's own citations, labelled by
 *  where they point. */
export const questionCitationOptions = (
  citations: Citation[],
  fieldId: string,
): { label: string; value: string }[] =>
  citations
    .filter((citation) => citation.fieldId === fieldId)
    .map((citation) => ({
      label: citation.cite || citation.text.slice(0, 40),
      value: citation.id,
    }))

export const questionStatusBadge = (
  question: PolicyQuestion,
  side: RailSide,
): { label: string; color: 'neutral' | 'success' | 'warning' } =>
  question.closedAt
    ? { label: 'Closed', color: 'neutral' }
    : question.answer
      ? {
          label: side === 'scoping' ? 'Answered — with design' : 'Answered',
          color: 'success',
        }
      : {
          label: side === 'scoping' ? 'Needs your answer' : 'With scoping',
          color: 'warning',
        }

/** The row's current head version: the newest one not undone. */
export const activeHeadVersionId = (
  versions: ContractVersion[],
): string | undefined => versions.find((version) => !version.undone)?.id

/** Rows the contract would hold back the hand-over for, in rail order. */
export const sortHoldRowsBySteps = (
  holdIds: readonly string[],
  stepIds: readonly string[],
): string[] => {
  const rank = (id: string) => {
    const at = stepIds.indexOf(id)
    return at === -1 ? stepIds.length : at
  }
  return [...holdIds].toSorted((a, b) => rank(a) - rank(b))
}

/** The next hold row after `currentId`, wrapping round; `undefined` when
 *  there are none left. */
export const nextOpenRowId = (
  openIds: readonly string[],
  currentId: string,
): string | undefined =>
  openIds.length
    ? openIds[(openIds.indexOf(currentId) + 1) % openIds.length]
    : undefined

export const answerPlainText = (value: AnswerValue | undefined): string => {
  if (Array.isArray(value)) return value.join(' ')
  if (typeof value === 'string') return value
  return value ? `${value.choice} ${value.other ?? ''}` : ''
}

/** A row's open questions from design, oldest asked first. */
export const unansweredQuestions = (
  questions: readonly PolicyQuestion[],
): PolicyQuestion[] =>
  questions.filter((question) => question.answer === null && !question.closedAt)

/** Every row but the six required ones can be marked not applicable (S-N6). */
export const isRowSkippable = (
  row: ContractRowView | undefined,
  editable: boolean,
): boolean => editable && row !== undefined && !isRequired(row.field)

/** A PDF selection cited into a row — `room.addCitation` stamps the id, `by`
 *  and `at`. */
export const selectionCitation = (
  fieldId: string,
  selection: PdfSelection,
  documentHash: string,
  documentId: string | null,
): Omit<Citation, 'id' | 'by' | 'at'> => ({
  fieldId,
  kind: 'source',
  text: selection.text,
  referenceDocumentId: documentId,
  excerptId: null,
  cite: `Page ${selection.page}`,
  anchor: { documentHash, pageIndex: selection.page - 1, rects: selection.rects },
})

/** A PaAPI excerpt cited into a row — `room.addCitation` stamps the id, `by`
 *  and `at`. */
export const excerptCitation = (
  fieldId: string,
  excerpt: { id: string; excerpt: string; location_reference?: string | null },
  documentId: string | null,
): Omit<Citation, 'id' | 'by' | 'at'> => ({
  fieldId,
  kind: 'source',
  text: excerpt.excerpt,
  referenceDocumentId: documentId,
  excerptId: excerpt.id,
  cite: excerpt.location_reference ?? '',
  anchor: null,
})

/** A question about a row this policy does not ask has no row to answer it
 *  in, so it is answered in the "back with you" inventory instead. */
export const unaskedQuestionFieldIds = (
  questions: readonly PolicyQuestion[],
  rows: readonly ContractRowView[],
): string[] => {
  const openFieldIds = questions
    .filter((question) => question.answer === null)
    .map((question) => question.fieldId)
  return [
    ...new Set(
      openFieldIds.filter(
        (fieldId) => !rows.some((row) => row.field.id === fieldId),
      ),
    ),
  ]
}
